"use client";

import { ChevronDownIcon } from "@chakra-ui/icons";
import { Button, Menu, MenuButton, MenuItem, MenuList } from "@chakra-ui/react";
import type React from "react";
import { useThemeTokens } from "@/lib/theme/tokens";

export interface SelectionAction {
  label: string;
  onClick: () => void;
  isDisabled?: boolean;
}

export interface SelectionActionsProps {
  actions: SelectionAction[];
  isDisabled?: boolean;
  buttonText?: string;
  variant?: "outline" | "solid" | "ghost";
  size?: "sm" | "md" | "lg";
}

export const SelectionActions: React.FC<SelectionActionsProps> = ({
  actions,
  isDisabled = false,
  buttonText = "Selection Actions",
  variant = "outline",
  size = "md",
}) => {
  const theme = useThemeTokens();

  return (
    <Menu>
      <MenuButton
        as={Button}
        rightIcon={<ChevronDownIcon />}
        variant={variant}
        size={size}
        isDisabled={isDisabled}
        fontWeight="normal"
        sx={{
          // Override data-active attribute styles (when menu is open)
          "&[data-active]": {
            bg: "transparent !important",
          },
          "&[data-active]:hover": {
            bg: "transparent !important",
          },
          "&[data-active]:focus": {
            bg: "transparent !important",
          },
          // Disabled buttons should not change background on hover, only cursor
          "@media (hover: hover)": {
            "&:hover:disabled": {
              bg: "transparent !important",
              borderColor: `${theme.borderGlass.default} !important`,
              cursor: "not-allowed",
            },
          },
          // Ensure disabled state has transparent background
          "&:disabled": {
            bg: "transparent !important",
            cursor: "not-allowed",
          },
          "&:disabled:hover": {
            bg: "transparent !important",
          },
        }}
      >
        {buttonText}
      </MenuButton>
      <MenuList>
        {actions.map((action, index) => (
          <MenuItem
            key={index}
            onClick={action.onClick}
            isDisabled={action.isDisabled}
          >
            {action.label}
          </MenuItem>
        ))}
      </MenuList>
    </Menu>
  );
};
